import { useCurrencyHelpers } from '@plato/app-shared/hooks/use-currency-helpers';
import { InsInsightOutcomeResponse, InsInsightOutcomeStatus } from '@plato/data/prisma';
import { lexicalToPlainText } from '@plato/node-util/rich-text/lexical-to-plain-text';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import { Dialog, DialogContent } from '@plato/ui/containers/dialog';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightOutcomeNotRelevantFormValues } from '../../../components/forms/insight-outcome-not-relevant-form.tsx';
import { InsightOutcomeNotRelevantForm } from '../../../components/forms/insight-outcome-not-relevant-form.tsx';
import type { InsightOutcomeResolvedFormValues } from '../../../components/forms/insight-outcome-resolved-form.tsx';
import { InsightOutcomeResolvedForm } from '../../../components/forms/insight-outcome-resolved-form.tsx';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { InsightDialog } from '../../insights-common/components/insight-dialog.tsx';
import type { SendAnalyticsEventHandler } from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { getInsightEntityName } from '../../insights-common/lib/insight-entity-name.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';

export type CustomerDetailInsightDialogsProps = {
    insightData: InsightListItem;
    open: boolean;
    onClose: () => void;
    onAnalyticsEvent: SendAnalyticsEventHandler;
};

/**
 * The detail dialog an insight card opens, plus the resolve ("Erledigen") and not-relevant outcome
 * modals it leads to. "Erledigen" will become "Auf die Agenda".
 */
export function CustomerDetailInsightDialogs({
    insightData,
    open,
    onClose,
    onAnalyticsEvent,
}: CustomerDetailInsightDialogsProps): ReactElement {
    const { t } = useTranslation();

    const { insight } = insightData;
    // Without a category the backend creates no business rule, so the toast body must not
    // promise "{{category}} paused for N months" (RETAIN-813).
    const hasCategory = insight.actionable_entity_type === 'WEB_CATEGORY';
    const entityName = getInsightEntityName(t, insightData);
    const insightTypeName = getInsightName(t, insight);

    const getOutcomeToastCopy = (response: InsInsightOutcomeResponse): { title: string; body: string } => {
        const bodyArgs = { category: entityName, recommendationType: insightTypeName };
        switch (response) {
            case InsInsightOutcomeResponse.BUYS_ELSEWHERE:
                return {
                    title: t('outcome_toast__buys_elsewhere__title', { ns: 'insights' }),
                    body: t('outcome_toast__buys_elsewhere__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.INCORRECT:
                return {
                    title: t('outcome_toast__incorrect__title', { ns: 'insights' }),
                    body: t('outcome_toast__incorrect__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.INTERESTED:
                return {
                    title: t('outcome_toast__interested__title', { ns: 'insights' }),
                    body: t('outcome_toast__interested__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.NO_NEED:
                return {
                    title: t('outcome_toast__no_need__title', { ns: 'insights' }),
                    body: t('outcome_toast__no_need__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.NORMAL:
                return {
                    title: t('outcome_toast__normal__title', { ns: 'insights' }),
                    body: t('outcome_toast__normal__body', { ns: 'insights', ...bodyArgs }),
                };
        }
    };

    const [isOutcomeModalOpen, setIsOutcomeModalOpen] = useState(false);
    const [outcomeStatus, setOutcomeStatus] = useState<InsInsightOutcomeStatus>(
        InsInsightOutcomeStatus.RESOLVED,
    );

    const { toCurrencyString } = useCurrencyHelpers();
    const estimatedPotential = toCurrencyString(roundAbsToHundred(insight.absolute_number ?? 0));

    const apiUtils = api.useUtils();
    const { toast } = useToast();
    const { mutate: deleteInsightOutcome } =
        api.platoUser.command.insights.postInsightActions.deleteInsightOutcome.useMutation({
            onSuccess: () => {
                void apiUtils.platoUser.query.insights.getManyInsights.getListWithFilters.invalidate({
                    customerId: insight.customer_id,
                });
                void apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate({
                    customerId: insight.customer_id,
                });
                void apiUtils.platoUser.getManyCustomers.getListWithFilters.reset();
            },
        });

    // The outcome form is presentational; this component owns the submission, its success
    // toast (with undo) and the cache invalidation that follows.
    const { mutate: submitInsightOutcome, isPending: isSubmittingOutcome } =
        api.platoUser.command.insights.postInsightActions.submitInsightOutcome.useMutation({
            onSuccess: (data, variables) => {
                void apiUtils.platoUser.query.insights.getManyInsights.getListWithFilters.invalidate({
                    customerId: insight.customer_id,
                });
                void apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                void apiUtils.platoUser.getManyCustomers.getListWithFilters.reset();

                const outcomeId = data.id;
                const outcomeToast = getOutcomeToastCopy(variables.response);
                const successToast = toast({
                    icon: 'success',
                    title: outcomeToast.title,
                    description: (
                        <>
                            {hasCategory
                                ? outcomeToast.body
                                : t('outcome_toast__saved_body', {
                                      ns: 'insights',
                                      category: entityName,
                                      recommendationType: insightTypeName,
                                  })}{' '}
                            <LinkText
                                as="button"
                                onClick={() => {
                                    successToast.dismiss();
                                    deleteInsightOutcome({ outcomeId });
                                }}
                            >
                                {t('outcome_toast__undo', { ns: 'insights' })}
                            </LinkText>
                        </>
                    ),
                });

                onAnalyticsEvent({
                    event:
                        variables.status === InsInsightOutcomeStatus.RESOLVED
                            ? 'sales_insight_completed'
                            : 'sales_insight_not_relevant',
                    payload: {
                        reason_given_dropdown: variables.response,
                        reasons_given_dropdown: variables.contextTags ?? [],
                        reason_given_text: lexicalToPlainText(variables.textRich ?? null) ?? '',
                        sales_insight_id: insight.id,
                    },
                });

                setIsOutcomeModalOpen(false);
            },
        });

    return (
        <>
            <Dialog
                open={isOutcomeModalOpen}
                onClose={() => {
                    setIsOutcomeModalOpen(false);
                }}
            >
                <DialogContent className="sm:max-w-2xl">
                    {outcomeStatus === InsInsightOutcomeStatus.RESOLVED ? (
                        <InsightOutcomeResolvedForm
                            insight={insight}
                            estimatedPotential={estimatedPotential}
                            productCategoryTitle={entityName}
                            isSubmitting={isSubmittingOutcome}
                            onSubmit={(data: InsightOutcomeResolvedFormValues): void => {
                                submitInsightOutcome({
                                    insightId: insight.id,
                                    status: InsInsightOutcomeStatus.RESOLVED,
                                    response: data.response,
                                    textRich: data.textRich ?? undefined,
                                });
                            }}
                            onClose={() => {
                                setIsOutcomeModalOpen(false);
                            }}
                        />
                    ) : (
                        <InsightOutcomeNotRelevantForm
                            insight={insight}
                            estimatedPotential={estimatedPotential}
                            productCategoryTitle={entityName}
                            isSubmitting={isSubmittingOutcome}
                            onSubmit={(data: InsightOutcomeNotRelevantFormValues): void => {
                                submitInsightOutcome({
                                    insightId: insight.id,
                                    status: InsInsightOutcomeStatus.NOT_RELEVANT,
                                    response: data.response,
                                    textRich: data.textRich ?? undefined,
                                    contextTags: data.contextTags.length > 0 ? data.contextTags : undefined,
                                });
                            }}
                            onClose={() => {
                                setIsOutcomeModalOpen(false);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <InsightDialog
                open={open}
                onClose={onClose}
                insightData={insightData}
                onResolve={(): void => {
                    setOutcomeStatus(InsInsightOutcomeStatus.RESOLVED);
                    setIsOutcomeModalOpen(true);
                }}
                onNotRelevant={(): void => {
                    setOutcomeStatus(InsInsightOutcomeStatus.NOT_RELEVANT);
                    setIsOutcomeModalOpen(true);
                }}
                onAnalyticsEvent={onAnalyticsEvent}
            />
        </>
    );
}
